import { BarChart3, Bell, CreditCard, MessageSquare, Camera, CheckCircle } from 'lucide-react';

export const ExperienceBridge = () => {
  return (
    <section id="experience" data-testid="experience-bridge-section" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0F1D32]" />
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48Y2lyY2xlIGN4PSIzMCIgY3k9IjMwIiByPSIxIi8+PC9nPjwvZz48L3N2Zz4=')]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-amber-400 font-body text-xs font-semibold uppercase tracking-[0.25em] mb-4">
              Client Experience
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-700 text-white leading-tight italic">
              Your Project. <br />One Place.
            </h2>
            <p className="mt-6 text-base text-white/50 font-body leading-relaxed max-w-lg">
              HDH clients get more than a pool. Track progress, view updates,
              manage payments, and communicate directly — all from a single,
              connected experience powered by Pool Ops Hub.
            </p>

            <div className="grid grid-cols-2 gap-4 mt-10">
              {[
                { icon: BarChart3, title: 'Project Dashboard', desc: 'Track milestones and progress' },
                { icon: Bell, title: 'Updates & Photos', desc: 'Real-time build visibility' },
                { icon: CreditCard, title: 'Payments', desc: 'View and pay invoices' },
                { icon: MessageSquare, title: 'Direct Messaging', desc: 'Talk to your team directly' },
              ].map((item) => (
                <div
                  key={item.title}
                  data-testid={`experience-feature-${item.title.toLowerCase().replace(/\s+/g, '-')}`}
                  className="p-4 rounded-sm bg-white/5 border border-white/10"
                >
                  <item.icon className="h-5 w-5 text-amber-400 mb-2" />
                  <p className="text-sm font-semibold text-white font-body">{item.title}</p>
                  <p className="text-xs text-white/40 font-body mt-0.5">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Mock Dashboard */}
          <div className="relative">
            <div className="bg-white/[0.07] backdrop-blur-sm rounded-sm border border-white/10 p-6 space-y-5">
              <div className="flex items-center justify-between pb-4 border-b border-white/10">
                <div>
                  <p className="text-[10px] text-white/30 font-body uppercase tracking-wider">Project</p>
                  <p className="text-sm font-semibold text-white font-body">Anderson Residence — Pool & Spa</p>
                </div>
                <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase tracking-wider">
                  On Track
                </span>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-white/40 font-body">Overall Progress</p>
                  <p className="text-xs font-bold text-white font-body">68%</p>
                </div>
                <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="w-[68%] h-full bg-amber-500 rounded-full" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-sm bg-white/5 border border-white/10">
                  <p className="text-[10px] text-white/30 font-body uppercase">Next Milestone</p>
                  <p className="text-sm font-medium text-white font-body mt-1">Tile Installation</p>
                  <p className="text-xs text-white/40 font-body">Mar 20, 2025</p>
                </div>
                <div className="p-3 rounded-sm bg-white/5 border border-white/10">
                  <p className="text-[10px] text-white/30 font-body uppercase">Payments</p>
                  <p className="text-sm font-medium text-white font-body mt-1">$30,000 paid</p>
                  <p className="text-xs text-white/40 font-body">of $45,000 total</p>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <p className="text-[10px] text-white/30 font-body uppercase tracking-wider">Recent Updates</p>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-sky-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Camera className="h-3.5 w-3.5 text-sky-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white/80 font-body">Plumbing rough-in complete</p>
                    <p className="text-xs text-white/30 font-body">2 photos added — Mar 15</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-white/80 font-body">County inspection passed</p>
                    <p className="text-xs text-white/30 font-body">Mar 12</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
